import { AfterViewInit, Component, computed, ContentChildren, effect, input, InputSignal, model, OnChanges, OnInit, output, SimpleChanges } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { MenuItem, PrimeTemplate } from 'primeng/api';
import { SelectButton, SelectButtonChangeEvent } from 'primeng/selectbutton';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeaderDivisor } from '@/shared/components/layout/header-divisor/header-divisor';
import { BotaoAcoes, BotaoAcoesType } from '../botao-acoes/botao-acoes';
import { ButtonModule } from 'primeng/button';
import { delay, Subject } from 'rxjs';

export type TabContainerOptionType = {
    label: string;
    value: string;
    icon?: string;
    actions?: BotaoAcoesType;
};

export type TabContainerActionType = {
    label: string;
    icon?: string;
    disabled: boolean;
    type: 'select' | 'button';
    command?: () => void;
};

@Component({
    standalone: true,
    template: `@if (visible()) {
        <ng-content></ng-content>
    }`,
    selector: 'app-tab-content'
})
export class TabContent {
    value = input<string>('');
    visible = model<boolean>(false);
}

@Component({
    standalone: true,
    selector: 'app-tab-container',
    imports: [FormsModule, ReactiveFormsModule, PrimeTemplate, SelectButton, HeaderDivisor, BotaoAcoes, ButtonModule, NgTemplateOutlet],
    templateUrl: './tab-container.html',
    styleUrl: './tab-container.scss'
})
export class TabContainer implements OnInit, AfterViewInit, OnChanges {
    options: InputSignal<TabContainerOptionType[]> = input([] as TabContainerOptionType[]);
    useHeader = input<boolean>(true);
    useEditButton = input<boolean>(false);
    useEditActionsMenu = input<boolean>(false);
    /**
     * Desenha a porta de ENTRADA da edição (o botão "Editar" ou o menu de ações próprio). Desligar
     * mantém Salvar/Cancelar, que continuam sendo do container: serve para a tela que já tem um menu
     * de Ações e não quer um segundo botão só para "Editar" — quem abre a edição, nesse caso, é o
     * item do menu de fora, escrevendo em `modoEdicao` (que é `model`, e portanto bidirecional).
     *
     * Padrão `true`: quem não passa nada segue com o comportamento de sempre.
     */
    mostrarEntradaDeEdicao = input<boolean>(true);
    disableEdit = input(false);
    selected = model('');
    customActionsRecord = input<Record<string, TabContainerActionType>>({});

    onChanged = output<{ label: string; value: string }>();
    edicaoEnabled = output<{ tab: string; enable: boolean }>();
    salvar = output<string>();
    cancelar = output<string>();

    oldSelected: string = '';
    selectedOption: TabContainerOptionType | undefined;
    modoEdicao = model<boolean>(false);
    changeManager: Subject<void> = new Subject();
    customActions = computed(() => {
        return Object.values(this.customActionsRecord());
    });
    readonly editActions: MenuItem[] = [
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            styleClass: 'bg-blue-500',
            command: () => this.habilitarEdicao()
        }
    ];

    @ContentChildren(TabContent) tabContents!: TabContent[];

    constructor() {
        effect(() => {
            this.customActions = computed(() => Object.values(this.customActionsRecord()));
        });
    }

    ngOnInit(): void {
        this.setSelectedByIndex(0);
        this.storeOldSelected();
        if (this.selectedOption) this.onChanged.emit(this.selectedOption);

        this.changeManager.pipe(delay(5)).subscribe({
            next: this.changeStages.bind(this)
        });
    }

    ngAfterViewInit(): void {
        this.enableSelectedTabContent();
    }

    ngOnChanges(_changes: SimpleChanges): void {}

    onChange(_event: SelectButtonChangeEvent): void {
        this.changeManager.next();
    }

    changeStages() {
        this.disableOldSelectedContent();
        this.setSelectedByIndex(this.findSelectedIndex());
        if (this.selectedOption) this.onChanged.emit(this.selectedOption);
        this.storeOldSelected();
        this.enableSelectedTabContent();
    }

    findTabContent(value: string): TabContent | undefined {
        return this.tabContents?.find((tabContent) => tabContent.value() === value);
    }

    findSelectedIndex() {
        return this.options().findIndex((option) => option.value === this.selected());
    }

    setSelectedByIndex(index: number) {
        const option = this.options()[index];
        if (option) {
            this.selected.set(option.value);
            this.selectedOption = option;
        }
    }

    enableSelectedTabContent() {
        const tabContent = this.findTabContent(this.selected());
        if (tabContent) tabContent.visible.set(true);
    }

    disableOldSelectedContent() {
        const tabContent = this.findTabContent(this.oldSelected);
        if (tabContent) tabContent.visible.set(false);
    }

    storeOldSelected() {
        this.oldSelected = this.selected();
    }

    habilitarEdicao(): void {
        this.modoEdicao.set(true);
        this.edicaoEnabled.emit({ tab: this.selected(), enable: true });
    }

    salvarClicked(): void {
        this.modoEdicao.set(false);
        this.salvar.emit(this.selected());
    }

    cancelarClicked(): void {
        this.modoEdicao.set(false);
        this.cancelar.emit(this.selected());
    }
}
